import type { Maybe, AutopopulateStayAreaOperationFragment, AutopopulateStayAreaOperationsByEventIdQuery } from '@graphql/generated';
import { useAutopopulateStayAreaOperationsByEventIdQuery } from '@graphql/generated';
import { DEFAULT_POLL_SCHEDULE_MS, useAutoPopulationPolling as useBaseAutoPopulationPolling } from '@shared/utils/autoPopulation/useAutoPopulationPolling';
import type { UseAutoPopulationPollingReturn } from '@shared/utils/autoPopulation/useAutoPopulation.types';
import type { AutoPopulationResult, StayAreasContext } from '../stayAreasAutoPopulation.types';

interface UseAutoPopulationPollingOptions
  extends Readonly<{
    eventId?: Maybe<string>;
    shouldSkipAutoPopulationCheck: boolean;
    context: StayAreasContext;
    skipPolling?: boolean;
  }> {}

/**
 * Configuration for extracting and mapping stay area operations.
 */
const pollingConfig = {
  extractOperations: (data: AutopopulateStayAreaOperationsByEventIdQuery | undefined) => {
    return data?.autopopulateStayAreaOperationsByEventId?.operations;
  },

  mapOperationResults: (operation: AutopopulateStayAreaOperationFragment): ReadonlyArray<AutoPopulationResult> => {
    return operation.result ?? [];
  },

  getOperationStatus: (operation: AutopopulateStayAreaOperationFragment): string => {
    return operation.status;
  },
} as const;

/**
 * Hook to poll auto-populate stay area operations status.
 * Wraps the base polling hook with stay areas-specific configuration.
 */
export const useStayAreasAutoPopulationPolling = (options: UseAutoPopulationPollingOptions): UseAutoPopulationPollingReturn<AutoPopulationResult> => {
  const { eventId, shouldSkipAutoPopulationCheck, context, skipPolling } = options;

  // Calculate skip condition for the query
  const shouldSkip = !eventId || shouldSkipAutoPopulationCheck || skipPolling;

  // Call query hook unconditionally at top level
  const queryResult = useAutopopulateStayAreaOperationsByEventIdQuery({
    variables: { payload: { eventId: eventId! } },
    skip: shouldSkip,
    batchMode: 'fast',
  });

  return useBaseAutoPopulationPolling<AutopopulateStayAreaOperationsByEventIdQuery, AutopopulateStayAreaOperationFragment, AutoPopulationResult, StayAreasContext>({
    eventId,
    shouldSkipAutoPopulationCheck,
    context,
    queryResult,
    config: pollingConfig,
    pollScheduleMs: DEFAULT_POLL_SCHEDULE_MS,
  });
};
